// Conditional formatting engine (items 62-64): multiple prioritized rules, Excel-style
// rule types, data bars, icon sets, color scales, formula rules, cell/row/widget scopes.
// Rules are evaluated on the CURRENT query result, so formatting reacts to filters,
// slicers, drilldown and drillthrough automatically.

const NAMED = { green: "#00C2A8", red: "#E85D6A", amber: "#F59E0B", blue: "#2B4EFF" };
export const asColor = (c, fb = null) => (c ? NAMED[c] || c : fb);

export function hexToRgba(hex, a = 1) {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex || "");
  if (!m) return hex;
  return `rgba(${parseInt(m[1], 16)}, ${parseInt(m[2], 16)}, ${parseInt(m[3], 16)}, ${a})`;
}

function lerpColor(c1, c2, t) {
  const p = (h) => /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(h || "");
  const a = p(c1), b = p(c2);
  if (!a || !b) return c2;
  const ch = (i) => Math.round(parseInt(a[i], 16) + (parseInt(b[i], 16) - parseInt(a[i], 16)) * t);
  return `rgb(${ch(1)}, ${ch(2)}, ${ch(3)})`;
}

export function normalizeRule(r) {
  const color = asColor(r.color, "#E85D6A");
  // Composable layers (item 21/25): rule.effects = { text, bg, font, icon, databar, scale }
  // Legacy rules carried a single `effect` string — normalize into layers, preserving visuals.
  let ef = r.effects;
  if (!ef) {
    const e = r.effect || "highlight";
    if (e === "text") ef = { text: { on: true, color }, font: { on: true, bold: true } };
    else if (e === "bg") ef = { bg: { on: true, color: r.bg ? asColor(r.bg) : hexToRgba(color, 0.22) } };
    else if (e === "icon") ef = { icon: { on: true, icon: r.icon || "circle", color: asColor(r.iconColor, color), position: "left" } };
    else if (e === "databar") ef = { databar: { on: true, color } };
    else if (e === "scale") ef = { scale: { on: true, min: r.scaleMin || "#FDE68A", max: r.scaleMax || "#00C2A8" } };
    else ef = { text: { on: true, color }, bg: { on: true, color: hexToRgba(r.bg ? asColor(r.bg) : color, 0.14) }, font: { on: true, bold: true } };
  }
  return {
    enabled: r.enabled !== false,
    scope: r.scope || "cell",
    target: r.target || "value",
    type: r.type || "value",
    op: r.op || "gt",
    value: r.value,
    value2: r.value2,
    n: Number(r.n) || 10,
    formula: r.formula || "",
    stop: !!r.stopIfTrue,
    outlineColor: ef.bg?.color && String(ef.bg.color).startsWith("#") ? ef.bg.color : (ef.text?.color || ef.icon?.color || color),
    effects: ef,
  };
}

// Editor helper: the effects layers of a raw rule (legacy rules included).
export function ruleEffects(r) {
  return normalizeRule(r || {}).effects;
}

// --- restricted formula evaluation (row-scoped, no code generation) ---
// Hand-rolled tokenizer + recursive-descent parser: literals, row-field identifiers,
// arithmetic, comparisons, &&/||/!, ternary and parentheses. Nothing else can execute.
const TOKEN_RE = /(\d+(?:\.\d+)?)|('(?:[^'\\]|\\.)*'|"(?:[^"\\]|\\.)*")|([A-Za-z_][A-Za-z0-9_]*)|(===|!==|==|!=|<=|>=|&&|\|\||[-+*/%<>!?():])|(\s+)|(.)/g;

function tokenizeFormula(src) {
  const out = [];
  let m;
  TOKEN_RE.lastIndex = 0;
  while ((m = TOKEN_RE.exec(src)) !== null) {
    if (m[5]) continue;               // whitespace
    if (m[6]) return null;            // unsupported character -> reject whole formula
    if (m[1]) out.push({ t: "num", v: Number(m[1]) });
    else if (m[2]) out.push({ t: "str", v: m[2].slice(1, -1).replace(/\\(.)/g, "$1") });
    else if (m[3]) out.push({ t: "id", v: m[3] });
    else out.push({ t: "op", v: m[4] });
  }
  return out;
}

function parseFormula(tokens) {
  let pos = 0;
  const eatOp = (v) => { const tk = tokens[pos]; if (tk && tk.t === "op" && tk.v === v) { pos++; return true; } return false; };
  const bin = (next, ops) => () => {
    let left = next();
    for (;;) {
      const tk = tokens[pos];
      if (tk && tk.t === "op" && ops.includes(tk.v)) { pos++; left = { k: tk.v, a: left, b: next() }; }
      else return left;
    }
  };
  const primary = () => {
    const tk = tokens[pos];
    if (!tk) throw new Error("eof");
    if (tk.t === "num" || tk.t === "str") { pos++; return { k: "lit", v: tk.v }; }
    if (tk.t === "id") {
      pos++;
      if (tk.v === "true") return { k: "lit", v: true };
      if (tk.v === "false") return { k: "lit", v: false };
      if (tk.v === "null") return { k: "lit", v: null };
      return { k: "ref", v: tk.v };
    }
    if (eatOp("(")) { const e = ternary(); if (!eatOp(")")) throw new Error(")"); return e; }
    throw new Error("token");
  };
  const unary = () => {
    if (eatOp("!")) return { k: "!", a: unary() };
    if (eatOp("-")) return { k: "neg", a: unary() };
    if (eatOp("+")) return unary();
    return primary();
  };
  const mul = bin(unary, ["*", "/", "%"]);
  const add = bin(mul, ["+", "-"]);
  const cmp = bin(add, ["<", "<=", ">", ">="]);
  const eq = bin(cmp, ["==", "!=", "===", "!=="]);
  const and = bin(eq, ["&&"]);
  const or = bin(and, ["||"]);
  function ternary() {
    const c = or();
    if (eatOp("?")) { const t = ternary(); if (!eatOp(":")) throw new Error(":"); return { k: "?:", c, t, f: ternary() }; }
    return c;
  }
  const ast = ternary();
  if (pos !== tokens.length) throw new Error("trailing");
  return ast;
}

/* eslint-disable eqeqeq */
function evalNode(n, row) {
  switch (n.k) {
    case "lit": return n.v;
    case "ref": return row ? row[n.v] : undefined;
    case "!": return !evalNode(n.a, row);
    case "neg": return -evalNode(n.a, row);
    case "?:": return evalNode(n.c, row) ? evalNode(n.t, row) : evalNode(n.f, row);
    case "&&": return evalNode(n.a, row) && evalNode(n.b, row);
    case "||": return evalNode(n.a, row) || evalNode(n.b, row);
    default: break;
  }
  const a = evalNode(n.a, row), b = evalNode(n.b, row);
  switch (n.k) {
    case "*": return a * b;
    case "/": return a / b;
    case "%": return a % b;
    case "+": return a + b;
    case "-": return a - b;
    case "<": return a < b;
    case "<=": return a <= b;
    case ">": return a > b;
    case ">=": return a >= b;
    case "==": return a == b;       // loose equality kept for legacy formulas
    case "!=": return a != b;
    case "===": return a === b;
    case "!==": return a !== b;
    default: return false;
  }
}
/* eslint-enable eqeqeq */

export function compileFormula(expr) {
  const e = (expr || "").trim();
  if (!e) return null;
  const tokens = tokenizeFormula(e);
  if (!tokens || !tokens.length) return null;
  let ast;
  try { ast = parseFormula(tokens); } catch { return null; }
  return (row) => {
    try { return evalNode(ast, row); } catch { return false; }
  };
}

function matchOp(op, v, a, b) {
  const n = Number(v), x = Number(a), y = Number(b);
  switch (op) {
    case "gt": return n > x;
    case "gte": return n >= x;
    case "lt": return n < x;
    case "lte": return n <= x;
    case "eq": return Number.isFinite(n) && Number.isFinite(x) && String(a).trim() !== "" ? n === x : String(v ?? "").toLowerCase() === String(a ?? "").toLowerCase();
    case "neq": return Number.isFinite(n) && Number.isFinite(x) && String(a).trim() !== "" ? n !== x : String(v ?? "").toLowerCase() !== String(a ?? "").toLowerCase();
    case "between": return Number.isFinite(n) && n >= Math.min(x, y) && n <= Math.max(x, y);
    case "not_between": return Number.isFinite(n) && !(n >= Math.min(x, y) && n <= Math.max(x, y));
    case "contains": return String(v ?? "").toLowerCase().includes(String(a ?? "").toLowerCase());
    case "starts_with": return String(v ?? "").toLowerCase().startsWith(String(a ?? "").toLowerCase());
    case "ends_with": return String(v ?? "").toLowerCase().endsWith(String(a ?? "").toLowerCase());
    default: return false;
  }
}

export function buildCondEval(rulesRaw, rows = []) {
  const rules = (rulesRaw || []).map(normalizeRule).filter((r) => r.enabled);
  if (!rules.length) return null;
  const stats = {};
  const colStats = (col) => {
    if (!stats[col]) {
      const vals = rows.map((r) => Number(r?.[col])).filter(Number.isFinite);
      const sortedDesc = [...vals].sort((a, b) => b - a);
      stats[col] = {
        min: vals.length ? Math.min(...vals) : 0,
        max: vals.length ? Math.max(...vals) : 0,
        mean: vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : 0,
        sortedDesc,
        sortedAsc: [...sortedDesc].reverse(),
      };
    }
    return stats[col];
  };
  const fcache = {};
  const evalFormula = (rule, row) => {
    if (!(rule.formula in fcache)) fcache[rule.formula] = compileFormula(rule.formula);
    const f = fcache[rule.formula];
    return f ? !!f(row) : false;
  };

  const matches = (rule, row, v) => {
    switch (rule.type) {
      case "blank": return v == null || v === "";
      case "not_blank": return !(v == null || v === "");
      case "top_n": {
        const s = colStats(rule.target);
        if (!s.sortedDesc.length) return false;
        const thr = s.sortedDesc[Math.min(rule.n, s.sortedDesc.length) - 1];
        const n = Number(v);
        return Number.isFinite(n) && n >= thr;
      }
      case "bottom_n": {
        const s = colStats(rule.target);
        if (!s.sortedAsc.length) return false;
        const thr = s.sortedAsc[Math.min(rule.n, s.sortedAsc.length) - 1];
        const n = Number(v);
        return Number.isFinite(n) && n <= thr;
      }
      case "above_avg": { const n = Number(v); return Number.isFinite(n) && n > colStats(rule.target).mean; }
      case "below_avg": { const n = Number(v); return Number.isFinite(n) && n < colStats(rule.target).mean; }
      case "formula": return evalFormula(rule, row);
      default: return matchOp(rule.op, v, rule.value, rule.value2);
    }
  };

  const effectFor = (rule) => {
    // Compose every enabled layer of a matched rule into one effect object.
    const ef = rule.effects || {};
    const out = {};
    if (ef.text?.on) out.color = ef.text.color;
    if (ef.bg?.on) out.bg = ef.bg.color;
    if (ef.font?.on) {
      if (ef.font.bold) out.bold = true;
      if (ef.font.italic) out.italic = true;
      if (ef.font.underline) out.underline = true;
      if (ef.font.strike) out.strike = true;
    }
    if (ef.icon?.on) { out.icon = ef.icon.icon || "circle"; out.iconColor = ef.icon.color; out.iconPos = ef.icon.position || "left"; }
    return out;
  };
  const MERGE_KEYS = ["color", "bg", "bold", "italic", "underline", "strike", "icon", "iconColor", "iconPos", "bar"];
  const mergeFx = (acc, fx) => {
    // first rule to set a property wins (rules run top-down); different layers never conflict
    if (!fx) return acc;
    const out = acc ? { ...acc } : {};
    MERGE_KEYS.forEach((k) => { if (out[k] === undefined && fx[k] !== undefined) out[k] = fx[k]; });
    if (fx.iconPos && out.icon === fx.icon) out.iconPos = out.iconPos ?? fx.iconPos;
    return out;
  };

  return {
    // every matching cell-scope rule contributes its layers; first-set-wins per property.
    // Scale & data-bar layers paint every numeric cell of the column (Excel behavior).
    cell(row, colKey) {
      let acc = null;
      const v = row?.[colKey];
      for (const rule of rules) {
        if (rule.scope !== "cell" || rule.target !== colKey) continue;
        const ef = rule.effects || {};
        if (ef.scale?.on) {
          const s = colStats(colKey); const n = Number(v);
          if (Number.isFinite(n)) {
            const t = s.max > s.min ? (n - s.min) / (s.max - s.min) : 1;
            const mid = ef.scale.mid;
            const bg = mid ? (t < 0.5 ? lerpColor(ef.scale.min, mid, t * 2) : lerpColor(mid, ef.scale.max, (t - 0.5) * 2)) : lerpColor(ef.scale.min, ef.scale.max, t);
            acc = mergeFx(acc, { bg });
          }
        }
        if (ef.databar?.on) {
          const s = colStats(colKey); const n = Number(v);
          if (Number.isFinite(n)) acc = mergeFx(acc, { bar: { pct: s.max > s.min ? Math.max(3, ((n - s.min) / (s.max - s.min)) * 100) : 100, color: ef.databar.color || "#2B4EFF" } });
        }
        const conditional = ef.text?.on || ef.bg?.on || ef.font?.on || ef.icon?.on;
        if (conditional && matches(rule, row, v)) {
          acc = mergeFx(acc, effectFor(rule));
          if (rule.stop) break;
        }
      }
      return acc;
    },
    // row-scope rules compose the same way (condition checked on rule.target column)
    row(rowObj) {
      let acc = null;
      for (const rule of rules) {
        if (rule.scope !== "row") continue;
        if (!matches(rule, rowObj, rowObj?.[rule.target])) continue;
        const fx = effectFor(rule);
        if (fx.bg === undefined && fx.color === undefined && !fx.bold && !fx.italic && !fx.underline && !fx.strike) fx.bg = hexToRgba(rule.outlineColor, 0.10);
        acc = mergeFx(acc, fx);
        if (rule.stop) break;
      }
      return acc;
    },
    // widget-scope: outline color when ANY row matches
    widgetOutline() {
      for (const rule of rules) {
        if (rule.scope !== "widget") continue;
        if (rows.some((r) => matches(rule, r, r?.[rule.target]))) return rule.outlineColor;
      }
      return null;
    },
  };
}

// React style for a cell/row effect object
export function condStyle(fx) {
  if (!fx) return undefined;
  const s = {};
  if (fx.color) s.color = fx.color;
  if (fx.bold) s.fontWeight = 700;
  else if (fx.color && fx.bold === undefined) s.fontWeight = 600;
  if (fx.italic) s.fontStyle = "italic";
  const deco = [fx.underline && "underline", fx.strike && "line-through"].filter(Boolean).join(" ");
  if (deco) s.textDecoration = deco;
  if (fx.bg) s.background = fx.bg;
  if (fx.bar) s.backgroundImage = `linear-gradient(90deg, ${hexToRgba(fx.bar.color, 0.28)} ${fx.bar.pct}%, transparent ${fx.bar.pct}%)`;
  return Object.keys(s).length ? s : undefined;
}
